import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@components/common/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/common/form"
import { useMutation } from "@tanstack/react-query"
import { DictType } from "@pma/types"
import { Button } from "@components/common/button"
import axios from "axios"
import { useForm } from "react-hook-form"
import { EDIT_USER_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import { toast } from "@hooks/use-toast"
import { cn } from "@pma/utils/helpers"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { parseCookies } from "nookies"
import { useAuth } from "@stores/auth-store"
import { Lock } from "lucide-react"
import { PasswordInput } from "@components/common/password-input"
import Recaptcha from "../auth/recaptcha"
import { useEffect, useState } from "react"
import useTranslator from "@hooks/use-translator"
import { useNavigate, useSearchParams } from "react-router-dom"
import { PROFILE_PAGE_ROUTE } from "@pma/utils/page-routes"

interface UpdatePasswordFormProps extends React.HTMLAttributes<HTMLDivElement> {
  user: DictType
}

const formSchema = z
  .object({
    password: z
      .string()
      .nonempty({ message: "Password is required" })
      .min(6, { message: "Password must be at least 6 characters long" }),
    confirm_password: z
      .string()
      .nonempty({ message: "Confirm password is required" })
      .min(6, { message: "Confirm password must be at least 6 characters long" }),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: "Passwords don't match.",
    path: ["confirm_password"], // this targets the confirm_password field for the error
  })

export default function UpdatePasswordModal({
  user,
  className,
  ...props
}: UpdatePasswordFormProps) {
  const [recaptchaToken, setRecaptchaToken] = useState<DictType>({
    v2: "",
    v3: "",
  })
  const [submittedData, setSubmittedData] = useState({})
  const [v2Fallback, setV2Fallback] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [searchParam] = useSearchParams()
  const navigate = useNavigate()

  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const passwordExpired = searchParam.get("password_expired")

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const { authConfig, signIn } = useAuth()

  const authUserId = authConfig?.id

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: "",
      confirm_password: "",
    },
  })

  const { control, handleSubmit } = form

  const updatePassword = async (payload: DictType) => {
    if (!v2Fallback && !recaptchaToken.v3) {
      return toast({
        title: translate("Info"),
        description: translate("Please complete the reCAPTCHA"),
        variant: "info",
      })
    }

    if ((v2Fallback && !payload?.v2Token) || !recaptchaToken.v3) {
      return
    }
    const response = await axios.post(
      SERVER_URL + EDIT_USER_ENDPOINT,
      {
        password: payload?.password,
        phone_number: user?.phone_number,
        recaptcha_response: !v2Fallback ? recaptchaToken.v3 : undefined,
        recaptcha_response_v2: payload?.v2Token,
        recive_emails: false,
        recive_whatsapp: false,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const updatePasswordMutation = useMutation({
    mutationFn: (payload: DictType) => updatePassword(payload),
    mutationKey: ["update-password"],
    onSuccess: (data) => {
      setIsOpen(false)
      toast({
        title: translate("Success"),
        description: translate("Password updated successfully"),
        variant: "success",
      })

      signIn({ ...data })

      if (passwordExpired) {
        navigate(PROFILE_PAGE_ROUTE)
      }
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      if (errorMessage === "Invalid Captcha Response. Please try again.") {
        setV2Fallback(true)
      } else {
        toast({
          title: translate("Error"),
          description: translate(errorMessage || "Unable to update password at this time"),
          variant: "error",
        })
      }
    },
  })

  function onSubmit(data: z.infer<typeof formSchema>) {
    setSubmittedData(data)
    updatePasswordMutation.mutate(data)
  }
  const reSubmit = (token: string) => {
    updatePasswordMutation.mutate({ ...submittedData, v2Token: token })
  }

  useEffect(() => {
    if (passwordExpired) {
      setIsOpen(true)
    }
  }, [])

  if (authUserId !== user?.id && !passwordExpired) {
    // Restrict password update to own profile
    return <></>
  }

  return (
    <Dialog open={isOpen || !!passwordExpired} onOpenChange={setIsOpen}>
      <DialogTrigger
        onClick={() => setIsOpen(true)}
        className={cn(
          "flex w-fit items-center gap-1 text-sm text-primary",
          passwordExpired && "animate-pulse font-semibold text-red-500",
        )}
      >
        {translate("Update password")} <Lock size={15} />
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className='flex flex-col items-center justify-center gap-y-4 pb-2'>
            <h1 className='text-2xl font-semibold tracking-tight'>
              {translate("Create a new password")}
            </h1>
          </DialogTitle>
          <DialogDescription className='dark:text-white-fade space-y-2 pt-2 text-center font-medium text-zinc-900'>
            <div className={cn("grid gap-6 text-muted-foreground", className)} {...props}>
              <Form {...form}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className='grid gap-2'>
                    <FormField
                      control={control}
                      name='password'
                      render={({ field }) => (
                        <FormItem className='flex w-full flex-col space-y-2'>
                          <FormLabel
                            className={cn(
                              "w-full capitalize",
                              isHebrew ? "text-right" : "text-left",
                            )}
                          >
                            {translate("Password")}
                          </FormLabel>
                          <FormControl>
                            <PasswordInput
                              placeholder='********'
                              {...field}
                              className={cn(
                                "w-full rounded-full bg-white text-black",
                                isHebrew ? "text-right" : "text-left",
                              )}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={control}
                      name='confirm_password'
                      render={({ field }) => (
                        <FormItem className='flex w-full flex-col space-y-2'>
                          <FormLabel
                            className={cn(
                              "w-full capitalize",
                              isHebrew ? "text-right" : "text-left",
                            )}
                          >
                            {translate("Confirm password")}
                          </FormLabel>
                          <FormControl>
                            <PasswordInput
                              placeholder='********'
                              {...field}
                              className={cn(
                                "w-full rounded-full bg-white text-black",
                                isHebrew ? "text-right" : "text-left",
                              )}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      className='mt-2'
                      loading={updatePasswordMutation.isPending}
                      disabled={
                        updatePasswordMutation.isPending || updatePasswordMutation.isSuccess
                      }
                    >
                      {translate("Update password")}{" "}
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          </DialogDescription>
        </DialogHeader>

        {/* GOOGLE Recaptcha */}
        <Recaptcha setToken={setRecaptchaToken} v2={v2Fallback} reSubmit={reSubmit} />
      </DialogContent>
    </Dialog>
  )
}
